import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { InventoryItem, Supplier, InventoryCategory } from '@/types'

interface InventoryFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  item?: InventoryItem | null
}

export function InventoryFormModal({ isOpen, onClose, onSave, item }: InventoryFormModalProps) {
  const [formData, setFormData] = useState<Partial<InventoryItem>>({})
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [saving, setSaving] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    if (isOpen) {
      fetchSuppliersAndCategories()
      setFormData(item || {})
    }
  }, [isOpen, item])

  const fetchSuppliersAndCategories = async () => {
    if (!currentOrganization) return
    // Fetch suppliers
    const { data: suppliersData } = await supabase
      .from('suppliers')
      .select('*')
      .eq('organization_id', currentOrganization.id)
    if (suppliersData) setSuppliers(suppliersData)

    // Fetch categories
    const { data: categoriesData } = await supabase
      .from('inventory_categories')
      .select('*')
      .eq('organization_id', currentOrganization.id)
    if (categoriesData) setCategories(categoriesData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization) return
    setSaving(true)

    const { id, ...data } = formData

    const dataToSave = {
      ...data,
      organization_id: currentOrganization.id,
    }

    let result
    if (id) {
      result = await supabase.from('inventory_items').update(dataToSave).eq('id', id)
    } else {
      result = await supabase.from('inventory_items').insert(dataToSave)
    }

    const { error } = result

    if (!error) {
      onSave()
      onClose()
    } else {
      console.error(error)
      // You might want to show a toast notification here
    }
    setSaving(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          <DialogDescription>
            Fill in the details below to add a new product to your inventory.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input id="name" name="name" value={formData.name || ''} onChange={handleChange} className="col-span-3" required />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="quantity" className="text-right">Quantity</Label>
            <Input id="quantity" name="quantity" type="number" value={formData.quantity || ''} onChange={handleChange} className="col-span-3" required/>
          </div>
           <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="price" className="text-right">Price</Label>
            <Input id="price" name="price" type="number" step="0.01" value={formData.price || ''} onChange={handleChange} className="col-span-3" />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category_id" className="text-right">Category</Label>
            <Select name="category_id" onValueChange={(value) => handleSelectChange('category_id', value)} value={formData.category_id}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="supplier_id" className="text-right">Supplier</Label>
            <Select name="supplier_id" onValueChange={(value) => handleSelectChange('supplier_id', value)} value={formData.supplier_id}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select a supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Product'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
} 